import React, { useState } from 'react';
import { marked } from 'marked'; // Ensure you've installed marked
import './App.css';
import { generateQuestions, generatePrompt } from './api';

function App() {
  const [projectDescription, setProjectDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [additionalAnswers, setAdditionalAnswers] = useState({}); // New state for extra context
  const [loading, setLoading] = useState(false);
  const [finalPrompt, setFinalPrompt] = useState('');
  const [currentStep, setCurrentStep] = useState('description'); // 'description', 'questions', 'prompt'

  const handleSubmitDescription = async () => {
    if (!projectDescription.trim()) return;
    
    setAdditionalAnswers({}); // Reset additional context
    setLoading(true);
    try {
      const generatedQuestions = await generateQuestions(projectDescription);
      
      // Transform questions into the required format with options
      const formattedQuestions = generatedQuestions.map((q, index) => ({
        id: `q-${index}`,
        text: q.question,
        options: q.options,
        answered: false
      }));
      
      setQuestions(formattedQuestions);
      setCurrentStep('questions');
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate additional user experience questions
  const generateMoreQuestions = async () => {
    setLoading(true);
    try {
      // Get current context to send to API
      const context = {
        description: projectDescription,
        currentAnswers: answers,
        requestType: "additional" // Flag to indicate we want more questions
      };
      
      const additionalQuestions = await generateQuestions(context);
      
      if (additionalQuestions && additionalQuestions.length > 0) {
        // Format and add new questions
        const newQuestions = additionalQuestions.map((q, index) => ({
          id: `additional-${Date.now()}-${index}`,
          text: q.question,
          options: q.options,
          answered: false,
          isNew: true // Flag to highlight new questions
        }));
        
        // Append the new questions to the existing questions array
        setQuestions(prev => [...prev, ...newQuestions]);
        
        // Scroll to the bottom to show the new questions
        setTimeout(() => {
          const questionsContainer = document.querySelector('.questions-list');
          if (questionsContainer) {
            questionsContainer.scrollTop = questionsContainer.scrollHeight;
          }
        }, 100);
      } else {
        alert('No additional questions could be generated. Try modifying your project description.');
      }
    } catch (error) {
      console.error('Error generating additional questions:', error);
      alert('Failed to generate more questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate additional tech stack questions
  const generateMoreTechQuestions = async () => {
    setLoading(true);
    try {
      // Get current context to send to API
      const context = {
        description: projectDescription,
        currentAnswers: answers,
        requestType: "additional_tech" // Flag to indicate we want more questions
      };
      
      const additionalTechQuestions = await generateQuestions(context);
      
      if (additionalTechQuestions && additionalTechQuestions.length > 0) {
        // Format and add new questions
        const newQuestions = additionalTechQuestions.map((q, index) => ({
          id: `additional-tech-${Date.now()}-${index}`,
          text: q.question,
          options: q.options,
          answered: false,
          isNew: true // Flag to highlight new questions
        }));
        
        // Append the new questions to the existing questions array
        setQuestions(prev => [...prev, ...newQuestions]);
        
        // Scroll to the bottom to show the new questions
        setTimeout(() => {
          const questionsContainer = document.querySelector('.questions-list');
          if (questionsContainer) {
            questionsContainer.scrollTop = questionsContainer.scrollHeight;
          }
        }, 100);
      } else {
        alert('No additional questions could be generated. Try modifying your project description.');
      }
    } catch (error) {
      console.error('Error generating additional questions:', error);
      alert('Failed to generate more questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update handleSelectAnswer to allow multi-selection
  const handleSelectAnswer = (questionId, option) => {
    setAnswers(prevAnswers => {
      const current = prevAnswers[questionId] || [];
      const newAnswers = current.includes(option)
        ? current.filter(a => a !== option)
        : [...current, option];
      
      // Mark question as answered if at least one answer is selected
      setQuestions(prevQuestions =>
        prevQuestions.map(q =>
          q.id === questionId ? { ...q, answered: newAnswers.length > 0 } : q
        )
      );
      
      return { ...prevAnswers, [questionId]: newAnswers };
    });
  };

  // New handler to update additional text input
  const handleAdditionalContext = (questionId, value) => {
    setAdditionalAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // const generateFollowUpQuestions = async (questionId, selectedOption) => {
  //   // Only generate follow-ups for questions that might need them
  //   const parentQuestion = questions.find(q => q.id === questionId);
  //   if (!parentQuestion.triggersFollowUp) return;
    
  //   setLoading(true);
  //   try {
  //     // Get current context to send to API
  //     const context = {
  //       description: projectDescription,
  //       currentAnswers: { ...answers, [questionId]: selectedOption }
  //     };
      
  //     const followUps = await generateQuestions(context, questionId);
      
  //     if (followUps && followUps.length > 0) {
  //       // Format and add new questions
  //       const newQuestions = followUps.map((q, index) => ({
  //         id: `${questionId}-followup-${index}`,
  //         text: q.question,
  //         options: q.options,
  //         answered: false,
  //         parentId: questionId
  //       }));
        
  //       setQuestions(prev => [...prev, ...newQuestions]);
  //     }
  //   } catch (error) {
  //     console.error('Error generating follow-up questions:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleCreatePrompt = async () => {
    setLoading(true);
    try {
      // Ensure every question has an answer; assign "No Preference" if not answered
      const filledAnswers = { ...answers };
      questions.forEach((q) => {
        const currentAnswers = filledAnswers[q.id] || [];
        // Use "No Preference" if no answer selected
        const selected = currentAnswers.length > 0 ? currentAnswers : ["No Preference"];

        // If additional context exists, append to each answer
        if (additionalAnswers[q.id]) {
          filledAnswers[q.id] = selected.map(ans => `${ans} - ${additionalAnswers[q.id]}`);
        } else {
          filledAnswers[q.id] = selected;
        }
      });
      
      const prompt = await generatePrompt(projectDescription, filledAnswers);
      setFinalPrompt(prompt);
      setCurrentStep('prompt');
    } catch (error) {
      console.error('Error generating final prompt:', error);
      alert('Failed to generate the final prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    // Create a temporary DOM element to remove HTML tags
    const tempElement = document.createElement("div");
    tempElement.innerHTML = finalPrompt;
    const textOnly = tempElement.textContent || tempElement.innerText || "";
    
    navigator.clipboard.writeText(textOnly)
      .then(() => alert('Prompt copied to clipboard!'))
      .catch(err => console.error('Failed to copy:', err));
  };

  const resetApp = () => {
    setProjectDescription('');
    setQuestions([]);
    setAnswers({});
    setAdditionalAnswers({});
    setFinalPrompt('');
    setCurrentStep('description');
  };



  return (
    <div className="app-container">
      <header>
        <h1>Prompt Engineer</h1>
        <p>
          Use AI to create the perfect prompt for your next coding project. Prompt Engineer will
          ask you a series of questions to understand your project requirements and generate a
          detailed prompt for you to use as the initial input for an AI model. The more detailed 
          your prompt when you start a project, the better the results you'll get from the AI model!
        </p>
      </header>

      {currentStep === 'description' && (
        <div className="description-section">
          <h2>What do you want to build?</h2>
          <textarea
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
            placeholder="Describe your coding project in detail. What type of app is it? What problem does it solve? Who is it for?"
            rows={6}
            className='description-input'
          />
          <button 
            onClick={handleSubmitDescription}
            disabled={!projectDescription.trim() || loading}
            className="primary-button"
          >
            {loading ? 'Thinking...' : 'Generate Questions'}
          </button>
        </div>
      )}

      {currentStep === 'questions' && (
        <div className="questions-section">
          <h2>Let's refine your project</h2>
          <p>Answer these initial questions to help craft the perfect prompt. Generate more questions
            to futher define your project. Any questions where you do not have a preference, leave blank:</p>
          
          <div className="questions-list">
            {questions.map((question) => (
              <div key={question.id} className={`question-card ${question.answered ? 'answered' : ''} ${question.isNew ? 'new' : ''}`}>
                <h3>{question.text}</h3>
                <div className="options-grid">
                  {question.options.map((option, optIndex) => (
                    <button
                      key={`${question.id}-opt-${optIndex}`}
                      onClick={() => handleSelectAnswer(question.id, option)}
                      className={
                        answers[question.id] && answers[question.id].includes(option)
                        ? 'selected'
                        : ''
                      }
                      disabled={loading}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {/* Additional context text input */}
                <input
                  type="text"
                  className="additional-info-section"
                  placeholder="Additional context (optional)"
                  value={additionalAnswers[question.id] || ""}
                  onChange={(e) => {
                    handleAdditionalContext(question.id, e.target.value);
                    const newWidth = Math.min(e.target.value.length, 120);
                    e.target.style.width = newWidth + 'ch';
                  }}
                  disabled={loading}
                  style={{ maxWidth: "100%" }} // Ensure input doesn't exceed parent width
                />
              </div>
            ))}
          </div>
          
          <div className="action-buttons">
            <button 
              onClick={() => setCurrentStep('description')}
              className="secondary-button"
            >
              Back
            </button>
            <button
              onClick={generateMoreQuestions}
              disabled={loading}
              className="secondary-button"
            >
              {loading ? 'Generating...' : 'Further Refine the User Experience'}
            </button>
            <button
              onClick={generateMoreTechQuestions}
              disabled={loading}
              className="secondary-button"
            >
              {loading ? 'Generating...' : 'Further Refine the Tech Stack'}
            </button>
            <button
              onClick={handleCreatePrompt}
              disabled={loading}
              className="primary-button"
            >
              {loading ? 'Generating...' : 'Generate Prompt'}
            </button>
          </div>
        </div>
      )}

      {currentStep === 'prompt' && (
        <div className="prompt-section">
          <div className="prompt-section-header">
            <h2>Your Customized Prompt</h2>
          </div>
          <div 
            className="mini-html-container"
            dangerouslySetInnerHTML={{ __html: marked.parse(finalPrompt) }}>
          </div>
          <div className="action-buttons">
            <button 
              onClick={() => setCurrentStep('questions')}
              className="secondary-button"
            >
              Back
            </button>
            <button
              onClick={copyToClipboard}
              className="primary-button"
            >
              Copy to Clipboard
            </button>
            <button
              onClick={resetApp}
              className="secondary-button"
            >
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;